// iter585 — Settings → Data Hygiene tab.
// One-click UI for the iter584 payment_mode normalization migration.
// Audit-first (read-only) → optional Apply → optional Rollback.
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, CheckCircle2, Undo2, RefreshCw, AlertTriangle, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

function DistributionTable({ title, data, testid }) {
  const distribution = data?.current_distribution || {};
  const wouldChange = data?.would_change || 0;
  const unmapped = data?.unmapped || {};
  const totalDocs = Object.values(distribution).reduce((s, n) => s + Number(n || 0), 0);
  return (
    <Card className="border-stone-200" data-testid={`hygiene-table-${testid}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center justify-between">
          <span>{title}</span>
          <Badge variant="secondary" className="font-normal">{totalDocs} docs</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {Object.keys(distribution).length === 0 ? (
          <div className="text-xs text-muted-foreground italic">No documents in this collection yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-1.5 font-medium text-muted-foreground">Current value</th>
                  <th className="text-right py-1.5 font-medium text-muted-foreground">Count</th>
                  <th className="text-right py-1.5 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(distribution)
                  .sort((a, b) => b[1] - a[1])
                  .map(([raw, n]) => {
                    const isCanonical = ['cash', 'bank', 'credit'].includes(raw);
                    const isUnmapped = raw in unmapped;
                    const isMissing = raw === '<missing>';
                    return (
                      <tr key={raw} className="border-b hover:bg-muted/30">
                        <td className="py-1.5">
                          <code className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isCanonical ? 'bg-emerald-100 text-emerald-800' : isMissing ? 'bg-stone-200 text-stone-600' : 'bg-amber-100 text-amber-800'}`}>
                            {isMissing ? '(missing)' : raw}
                          </code>
                        </td>
                        <td className="py-1.5 text-right font-mono tabular-nums">{n}</td>
                        <td className="py-1.5 text-right text-[10px]">
                          {isCanonical ? (
                            <span className="text-emerald-700 inline-flex items-center gap-0.5"><CheckCircle2 size={10} /> Canonical</span>
                          ) : isUnmapped ? (
                            <span className="text-stone-600 inline-flex items-center gap-0.5"><AlertTriangle size={10} /> Unmapped — keep</span>
                          ) : isMissing ? (
                            <span className="text-stone-500">Skipped</span>
                          ) : (
                            <span className="text-amber-700">Will normalize →</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
        <div className={`flex items-center justify-between rounded-md px-2 py-1.5 text-[11px] ${wouldChange > 0 ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-testid={`hygiene-summary-${testid}`}>
          <span className="font-medium">
            {wouldChange > 0
              ? `${wouldChange} document${wouldChange === 1 ? '' : 's'} would be normalized by clicking Apply`
              : 'Clean — every value is already canonical'}
          </span>
          {Object.keys(unmapped).length > 0 && (
            <span className="text-stone-600">
              ({Object.keys(unmapped).length} unmapped value{Object.keys(unmapped).length === 1 ? '' : 's'} will be left alone)
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DataHygieneTab() {
  const [loading, setLoading] = useState(true);
  const [audit, setAudit] = useState(null);
  const [applying, setApplying] = useState(false);
  const [rolling, setRolling] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/payment-mode-audit');
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
      setAudit(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runAudit(); }, []);

  const totalWouldChange = audit
    ? Object.values(audit.report || {}).reduce((s, c) => s + Number(c?.would_change || 0), 0)
    : 0;
  const anyRolledBack = audit && Object.values(audit.report || {}).some((c) =>
    Object.keys(c?.current_distribution || {}).some((k) => !['cash', 'bank', 'credit', '<missing>'].includes(k) && !(k in (c?.unmapped || {})))
  );

  const apply = async () => {
    if (!window.confirm(`This will normalize ${totalWouldChange} document${totalWouldChange === 1 ? '' : 's'} across supplier_payments + expenses.\n\nOriginal values will be snapshotted so you can roll back if anything looks wrong.\n\nProceed?`)) return;
    setApplying(true);
    try {
      const r = await api.post('/admin/payment-mode-apply');
      const sp = r.data?.results?.supplier_payments?.updated || 0;
      const ex = r.data?.results?.expenses?.updated || 0;
      toast.success(`Normalized ${sp + ex} document${(sp + ex) === 1 ? '' : 's'} · ${sp} supplier_payments, ${ex} expenses`);
      await runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const rollback = async () => {
    if (!window.confirm('This will restore every document this migration touched back to its original payment_mode value.\n\nProceed?')) return;
    setRolling(true);
    try {
      const r = await api.post('/admin/payment-mode-rollback');
      const sp = r.data?.results?.supplier_payments?.restored || 0;
      const ex = r.data?.results?.expenses?.restored || 0;
      toast.success(`Rolled back ${sp + ex} document${(sp + ex) === 1 ? '' : 's'} · ${sp} supplier_payments, ${ex} expenses`);
      await runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rollback failed');
    } finally {
      setRolling(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="data-hygiene-tab">
      <div className="bg-gradient-to-r from-emerald-50 to-sky-50 border border-emerald-200 rounded-lg p-4 dark:from-emerald-950/20 dark:to-sky-950/20 dark:border-emerald-900">
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} className="text-emerald-700 shrink-0 mt-0.5" />
          <div className="space-y-1.5 min-w-0">
            <h3 className="text-sm font-semibold font-outfit">Payment-mode normalization</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Older versions of Smarto accepted payment_mode values like <code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded">Bank Transfer</code>, <code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded">Bank_Transfer</code>, <code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded">BANK</code>, etc. Newer code expects canonical <b>cash</b> / <b>bank</b> / <b>credit</b> values. This tool finds the variants in your data and normalizes them in one click, with a rollback safety net.
            </p>
            <p className="text-[11px] text-emerald-800 dark:text-emerald-300">
              <b>Safe to run:</b> the audit is read-only · Apply snapshots originals before changing anything · Rollback restores from the snapshot.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={runAudit}
          disabled={loading}
          data-testid="hygiene-refresh-btn"
        >
          {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
          Refresh audit
        </Button>
        {totalWouldChange > 0 && (
          <Button
            size="sm"
            onClick={apply}
            disabled={applying || rolling}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="hygiene-apply-btn"
          >
            {applying ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}
            Normalize {totalWouldChange} document{totalWouldChange === 1 ? '' : 's'}
          </Button>
        )}
        {anyRolledBack !== null && (
          <Button
            variant="outline"
            size="sm"
            onClick={rollback}
            disabled={applying || rolling}
            className="border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="hygiene-rollback-btn"
          >
            {rolling ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Undo2 size={14} className="mr-1" />}
            Roll back
          </Button>
        )}
      </div>

      {loading && !audit ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="animate-spin" size={16} /> Scanning your data…
        </div>
      ) : !audit ? (
        <div className="text-center py-8 text-sm text-muted-foreground">No audit data available.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DistributionTable title="Supplier payments" data={audit.report?.supplier_payments} testid="supplier_payments" />
          <DistributionTable title="Expenses" data={audit.report?.expenses} testid="expenses" />
        </div>
      )}

      {audit && (
        <p className="text-[10px] text-muted-foreground text-right">
          Last scanned · {(audit.scanned_at || '').replace('T', ' ').slice(0, 19)} UTC · Canonical values: {(audit.canonical_values || []).join(' / ')}
        </p>
      )}
    </div>
  );
}
